import axios from 'axios'
import type { BillingPeriod, TmsCheckoutTier, TmsTier, TmsPlanPrice } from '@numeo/types-core'

// ============================================
// TYPES — the /v1/billing/prices `data.tms` contract is single-sourced in @numeo/types
// (TmsTier, TmsPlanPrice, TmsCheckoutTier, BillingPeriod). Re-exported so existing feature imports
// (./tmsBilling.api) keep working.
// ============================================

export type { TmsTier, TmsPlanPrice }

// TMS billing interval — alias the single-sourced BillingPeriod so callers can keep importing TmsBilling.
export type TmsBilling = BillingPeriod

interface TmsPricesResponse {
    success: boolean
    data?: Record<TmsTier, TmsPlanPrice | null>
    message?: string
    error?: string
}

interface TmsCheckoutRequest {
    tier: TmsCheckoutTier
    billing: TmsBilling
    trucks: number
    /** Validated promotion-code id from the in-card promo field. */
    promotionCode?: string
}

// ============================================
// API
// ============================================

/**
 * TMS plans + section-based display features.
 * Sourced from the unified GET /v1/billing/prices response under `data.tms`
 * (one fetch returns every product family — extension plans and TMS).
 */
export const getTmsPrices = async (): Promise<TmsPricesResponse> => {
    const response = await axios.get<{ success: boolean; data?: { tms?: Record<TmsTier, TmsPlanPrice | null> }; message?: string }>('/billing/prices')
    return { success: response.data.success, data: response.data.data?.tms, message: response.data.message }
}

/** POST /v1/billing/tms/checkout — returns a Stripe Checkout Session URL to redirect to. */

interface TmsChangePlanResponse {
    success: boolean
    /** true → redirect to checkoutUrl (no card on file); false → updated in place, just refresh. */
    requiresCheckout?: boolean
    checkoutUrl?: string
    message?: string
    error?: string
}

/**
 * POST /v1/billing/tms/change-plan — mirrors the extension's change-plan flow. Card on file → updates
 * the TMS subscription in place; otherwise returns a checkout URL to collect one. No repeat trial.
 */
export const changeTmsPlan = async (body: TmsCheckoutRequest): Promise<TmsChangePlanResponse> => {
    const response = await axios.post<TmsChangePlanResponse>('/billing/tms/change-plan', body)
    return response.data
}

interface TmsCancelResponse {
    success: boolean
    /** Present when a paid sub is set to cancel at period end (ISO date string). */
    effectiveDate?: string
    message?: string
    error?: string
}

/**
 * POST /v1/billing/tms/cancel — cancels the TMS subscription (paid → at period end, trial →
 * immediately). Mirrors the extension's "Cancel plan".
 */
export const cancelTmsSubscription = async (): Promise<TmsCancelResponse> => {
    const response = await axios.post<TmsCancelResponse>('/billing/tms/cancel')
    return response.data
}
